import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PeaksSource } from '../lib/peaks.ts';
import { fullScale } from '../lib/peaks.ts';
import {
  absToX, xToAbs, snapToCoverage, type PlacedSegment, type Span,
} from '../lib/timeline.ts';
import type { Block } from '../api/client.ts';
import { snapTarget, midpointOf, type Gap } from '../lib/gaps.ts';

/** Grabbing a handle is easier than hitting a one-pixel line. */
const HANDLE_GRAB_PX = 7;
/** How far a press must travel to become a drag; anything less is a click. */
const DRAG_THRESHOLD_PX = 4;

export interface Selection {
  fromMs: number;
  toMs: number;
}

export type Handle = 'from' | 'to';

interface Props {
  block: Block;
  window: Span;
  placed: PlacedSegment[];
  /**
   * Peaks per segment id, each carrying the point in its file they start from —
   * the stored waveform begins at zero, a windowed read wherever it was asked
   * for. Absent until there is something to draw.
   */
  peaks: Map<number, PeaksSource>;
  positionMs: number | null;
  selection: Selection | null;
  onSelectionChange: (selection: Selection | null) => void;
  onSeek: (atMs: number) => void;
  /** Detected pauses, used to claim a released handle. */
  gaps?: Gap[];
  /** Measured drawing width, so the caller can ask for peaks to fit it. */
  onWidthChange?: (px: number) => void;
  height?: number;
}

/**
 * The waveform, drawn to a canvas.
 *
 * Each segment is painted at its own place on the clock, so a block with a hole
 * in it shows the hole, and the parts that survived can still be selected. A
 * segment whose waveform has not been built yet is drawn as a flat band rather
 * than left blank, so it is clear there is audio there.
 *
 * Canvas rather than SVG: a half hour is around 36,000 min/max pairs per
 * segment, and that many nodes would make dragging crawl.
 *
 * The document is right-to-left; this is a time axis, so it is pinned
 * left-to-right and every x here means "later to the right".
 */
export function Waveform({
  block, window: win, placed, peaks, positionMs, selection,
  onSelectionChange, onSeek, onWidthChange, gaps = [], height = 132,
}: Props): React.ReactElement {
  // Read inside the pointer handlers, which must not be rebuilt as gaps arrive.
  const gapsRef = useRef<Gap[]>(gaps);
  useEffect(() => { gapsRef.current = gaps; }, [gaps]);
  // Held down at release, snapping is off — for a deliberate mid-word cut.
  const altHeld = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // Drag state lives in refs, not React state. A pointermove can arrive in the
  // same tick as the pointerdown that started the drag, before a re-render has
  // happened, and reading stale state there loses the gesture.
  //
  // 'pending' is a press that has not moved yet: it may still turn out to be a
  // click, which jumps the playhead and must leave a marked clip alone.
  const draggingRef = useRef<Handle | 'new' | 'pending' | null>(null);
  const anchorRef = useRef<number>(0);
  const downXRef = useRef<number>(0);

  // Measured synchronously on mount, before paint. A ResizeObserver alone
  // reports asynchronously and in some embedded contexts never fires, which
  // would leave the canvas at its 300x150 default.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = (): void => {
      const measured = Math.floor(wrap.getBoundingClientRect().width);
      setWidth((current) => (measured > 0 && measured !== current ? measured : current));
    };
    measure();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(wrap);
    }
    globalThis.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      globalThis.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (width > 0) onWidthChange?.(width);
  }, [width, onWidthChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;

    const dpr = globalThis.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const style = getComputedStyle(canvas);
    const read = (name: string, fallback: string): string =>
      style.getPropertyValue(name).trim() || fallback;
    const waveColor = read('--wave', 'rgba(246,239,226,0.42)');
    const selectedColor = read('--wave-selected', '#F9B52B');
    const selectionBg = read('--wave-selection-bg', 'rgba(249,181,43,0.15)');
    const cursorColor = read('--wave-cursor', '#EC6A3C');
    const pendingColor = read('--wave-pending', 'rgba(246,239,226,0.16)');

    const mid = height / 2;
    const selFromX = selection ? absToX(selection.fromMs, win, width) : null;
    const selToX = selection ? absToX(selection.toMs, win, width) : null;

    if (selFromX !== null && selToX !== null) {
      ctx.fillStyle = selectionBg;
      ctx.fillRect(selFromX, 0, selToX - selFromX, height);
    }

    const msPerPixel = (win.toMs - win.fromMs) / width;

    for (let x = 0; x < width; x += 1) {
      const atMs = win.fromMs + x * msPerPixel;
      const holder = placed.find((p) => atMs >= p.fromMs && atMs < p.toMs);
      if (!holder) continue; // a gap stays empty

      const inSelection =
        selFromX !== null && selToX !== null && x >= selFromX && x <= selToX;

      const source = peaks.get(holder.segment.id);
      if (!source) {
        // Audio exists but its waveform has not been built yet.
        ctx.fillStyle = pendingColor;
        ctx.fillRect(x, mid - 6, 1, 12);
        continue;
      }

      // Indexed from where these peaks begin in the file, which is zero for the
      // stored waveform and the window's own start for a decoded one.
      const { data } = source;
      const pixelsPerSecond = data.sample_rate / data.samples_per_pixel;
      const secondsIn = (atMs - holder.fileStartMs) / 1000 - source.fromSec;
      const from = Math.floor(secondsIn * pixelsPerSecond);
      const to = Math.max(
        from + 1,
        Math.floor((secondsIn + msPerPixel / 1000) * pixelsPerSecond),
      );
      if (from < 0 || from >= data.length) continue;

      let min = 0;
      let max = 0;
      for (let i = from; i < to && i < data.length; i += 1) {
        const lo = data.data[i * 2] ?? 0;
        const hi = data.data[i * 2 + 1] ?? 0;
        if (lo < min) min = lo;
        if (hi > max) max = hi;
      }

      const scale = fullScale(data);
      ctx.fillStyle = inSelection ? selectedColor : waveColor;
      const top = mid - (max / scale) * mid;
      const bottom = mid - (min / scale) * mid;
      ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
    }

    // The clip's edges as solid lines, so a start that has no end yet is
    // still plainly there.
    if (selFromX !== null && selToX !== null) {
      ctx.fillStyle = selectedColor;
      ctx.fillRect(selFromX - 1, 0, 2, height);
      ctx.fillRect(selToX - 1, 0, 2, height);
    }

    // Playhead last, so it is never buried under the waveform.
    if (positionMs !== null) {
      ctx.fillStyle = cursorColor;
      ctx.fillRect(absToX(positionMs, win, width), 0, 1.5, height);
    }
  }, [placed, peaks, width, height, win, positionMs, selection]);

  const timeAt = useCallback(
    (clientX: number): number => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return win.fromMs;
      // Handles land on real audio rather than in a hole.
      return snapToCoverage(block, xToAbs(clientX - rect.left, win, rect.width));
    },
    [block, win],
  );

  function onPointerDown(event: React.PointerEvent<HTMLCanvasElement>): void {
    altHeld.current = event.altKey;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    event.currentTarget.setPointerCapture(event.pointerId);

    if (selection) {
      const fromX = absToX(selection.fromMs, win, rect.width);
      const toX = absToX(selection.toMs, win, rect.width);
      // Grabbing an existing edge adjusts it; the anchor becomes the far edge,
      // so dragging one handle past the other simply reverses them.
      if (Math.abs(x - fromX) <= HANDLE_GRAB_PX) {
        draggingRef.current = 'from';
        anchorRef.current = selection.toMs;
        return;
      }
      if (Math.abs(x - toX) <= HANDLE_GRAB_PX) {
        draggingRef.current = 'to';
        anchorRef.current = selection.fromMs;
        return;
      }
    }

    draggingRef.current = 'pending';
    anchorRef.current = timeAt(event.clientX);
    downXRef.current = event.clientX;
  }

  function onPointerMove(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!draggingRef.current) return;
    if (draggingRef.current === 'pending') {
      if (Math.abs(event.clientX - downXRef.current) < DRAG_THRESHOLD_PX) return;
      draggingRef.current = 'new';
    }
    const at = timeAt(event.clientX);
    const anchor = anchorRef.current;
    onSelectionChange({ fromMs: Math.min(anchor, at), toMs: Math.max(anchor, at) });
  }

  function onPointerUp(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!draggingRef.current) return;
    const mode = draggingRef.current;
    draggingRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    // A click without a drag means "play from here". Any clip already marked
    // stays exactly as it was.
    if (mode === 'pending') {
      onSeek(timeAt(event.clientX));
      return;
    }

    // On release, a handle within reach of a pause is claimed by it — landing
    // in the middle, clear of the words either side. Alt leaves it where it is.
    if (!selection || event.altKey || altHeld.current) return;
    const snapped = {
      fromMs: snapTarget(selection.fromMs, gapsRef.current),
      toMs: snapTarget(selection.toMs, gapsRef.current),
    };
    if (!snapped.fromMs && !snapped.toMs) return;
    onSelectionChange({
      fromMs: snapped.fromMs ? midpointOf(snapped.fromMs) : selection.fromMs,
      toMs: snapped.toMs ? midpointOf(snapped.toMs) : selection.toMs,
    });
  }

  return (
    <div className="waveform" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { draggingRef.current = null; }}
      />
    </div>
  );
}
